import React, { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Avatar utente riusabile: immagine profilo (GridFS, org-isolated) con
 * fallback alle iniziali colorate. Cache in-memory dei blob per sessione.
 */

export const AVATAR_PALETTE = [
  { bg: "#FEF3C7", fg: "#7A4600" },
  { bg: "#DCFCE7", fg: "#0E6B33" },
  { bg: "#DBEAFE", fg: "#1E4FBC" },
  { bg: "#FCE7F3", fg: "#831843" },
  { bg: "#E0E7FF", fg: "#3730A3" },
  { bg: "#FED7AA", fg: "#9A3412" },
];

export const avatarColorsFromName = (name) => {
  const s = String(name || "").trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

export const initialsFromName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const cache = new Map(); // key: `${userId}@${version}` -> Promise<objectURL|null>

const fetchAvatar = (userId, version) => {
  const key = `${userId}@${version || ""}`;
  if (!cache.has(key)) {
    const url = `/users/${userId}/avatar${version ? `?v=${encodeURIComponent(version)}` : ""}`;
    cache.set(
      key,
      api.get(url, { responseType: "blob" })
        .then(({ data }) => URL.createObjectURL(data))
        .catch(() => null)
    );
  }
  return cache.get(key);
};

export const invalidateAvatar = (userId) => {
  for (const key of [...cache.keys()]) {
    if (key.startsWith(`${userId}@`)) cache.delete(key);
  }
};

export const UserAvatar = ({ userId, name, version, size = 28, className = "", testId }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    // version === null → sappiamo già che l'avatar NON esiste: niente rete.
    // version === undefined → chiamante legacy che non conosce la versione.
    if (!userId || version === null || version === "") { setSrc(null); return undefined; }
    let alive = true;
    setSrc(null);
    fetchAvatar(userId, version).then((url) => { if (alive) setSrc(url); });
    return () => { alive = false; };
  }, [userId, version]);

  const style = { width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.36)) };
  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        data-testid={testId}
        className={`rounded-full object-cover shrink-0 ${className}`}
        style={style}
      />
    );
  }
  const colors = avatarColorsFromName(name);
  return (
    <span
      data-testid={testId}
      aria-hidden="true"
      className={`rounded-full inline-flex items-center justify-center font-semibold shrink-0 select-none ${className}`}
      style={{ ...style, background: colors.bg, color: colors.fg }}
    >
      {initialsFromName(name)}
    </span>
  );
};
